import { useEffect, useState } from "react";
import { ExternalLink, Maximize2 } from "lucide-react";
import { api } from "../lib/api.js";
import { logError } from "../lib/logger.js";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog.js";
import { Badge } from "./ui/badge.js";
import { Button } from "./ui/button.js";
import { docLabel, type DocSummary } from "./DocumentsOverviewStrip.js";

const STATUS_BADGE: Record<string, { tone: "success" | "error" | "warning" | "neutral"; label: string }> = {
  VERIFIED: { tone: "success", label: "Verified" },
  REJECTED: { tone: "error", label: "Rejected" },
  EXPIRED: { tone: "warning", label: "Expired" },
};

// One tile per document: fetches its own short-lived URL so every file loads in parallel.
function DocumentPreviewTile({ doc, onOpen }: { doc: DocSummary; onOpen: (doc: DocSummary) => void }) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const status = STATUS_BADGE[doc.status] ?? { tone: "neutral" as const, label: "Pending review" };
  const isImage = !!doc.mimeType?.startsWith("image/");
  const isPdf = doc.mimeType === "application/pdf";

  useEffect(() => {
    let cancelled = false;
    api.get<{ url: string }>(`/documents/${doc.id}/url`)
      .then(({ data }) => { if (!cancelled) setUrl(data.url); })
      .catch((err) => {
        if (!cancelled) setFailed(true);
        logError("AllDocumentsDialog.loadUrl", err, { docId: doc.id });
      });
    return () => { cancelled = true; };
  }, [doc.id]);

  return (
    <div className="flex min-w-0 flex-col gap-2 rounded-lg border border-surface-200 bg-surface-0 p-3">
      <div className="flex items-center justify-between gap-2">
        <span className="truncate text-sm font-semibold text-surface-900">{docLabel(doc)}</span>
        <Badge tone={status.tone}>{status.label}</Badge>
      </div>

      <div className="flex h-[360px] items-center justify-center overflow-hidden rounded-md border border-surface-100 bg-surface-50">
        {failed ? (
          <span className="text-xs text-surface-500">Couldn't load this file.</span>
        ) : !url ? (
          <span className="text-xs text-surface-400">Loading…</span>
        ) : isImage ? (
          <img src={url} alt={docLabel(doc)} className="max-h-full max-w-full object-contain" />
        ) : isPdf ? (
          <iframe src={url} title={docLabel(doc)} className="h-full w-full border-0" />
        ) : (
          <span className="text-xs text-surface-500">No inline preview for this file type.</span>
        )}
      </div>

      <div className="flex justify-end gap-2">
        {url && (
          <Button asChild variant="ghost" size="sm">
            <a href={url} target="_blank" rel="noopener noreferrer">
              <ExternalLink size={14} /> New tab
            </a>
          </Button>
        )}
        <Button variant="secondary" size="sm" onClick={() => onOpen(doc)}>
          <Maximize2 size={14} /> Open
        </Button>
      </div>
    </div>
  );
}

export function AllDocumentsDialog({
  docs, open, onOpenChange, onOpenDoc,
}: {
  docs: DocSummary[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onOpenDoc: (doc: DocSummary) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="h-[88vh] max-h-[88vh] w-[95vw] max-w-[95vw] content-start sm:w-[90vw] sm:max-w-[90vw]">
        <DialogHeader>
          <DialogTitle>All documents</DialogTitle>
          <DialogDescription>{docs.length} document{docs.length === 1 ? "" : "s"} on file</DialogDescription>
        </DialogHeader>
        {/* Only mount tiles while open, so presigned URLs are fetched on demand and fresh. */}
        {open && (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {docs.map((doc) => (
              <DocumentPreviewTile
                key={doc.id}
                doc={doc}
                onOpen={(d) => { onOpenChange(false); onOpenDoc(d); }}
              />
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
